//@flow
import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import ZoneMapActions from 'common/redux/actions/zoneMap';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import ZoneMapSelectors from 'common/redux/selectors/zoneMap';
import { PaginatedReactTable } from 'common/components/core/table/PaginatedTable';
import { withRouter } from 'common/components/core/router';
import VisitUtils from 'common/utils/VisitUtils';
import { Link } from 'react-router-dom';
import { CellDataTypes } from 'common/components/core/table/CellDataTypes';
import withStateHandlers from 'recompose/withStateHandlers';
import withHandlers from 'recompose/withHandlers';
import compose from 'recompose/compose';
import { Loader } from 'common/components/core/loader/Loader';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';

import theme from './MapVersionsTable.scss';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import { SizeEnum } from 'common/enums/UIEnums';
type ActionButtonStateType = {
    inProgress: boolean,
    error: ?string
};

const FreezeButtonHandlers = compose(
    withStateHandlers(
        ({
            inProgress: false,
            error: null
        }: ActionButtonStateType),
        {
            toggleInProgress: ({ inProgress }) => () => ({
                inProgress: !inProgress
            }),
            setError: () => (error: ?string) => ({
                error
            })
        }
    ),
    withHandlers({
        toggleFrozenStatus: ({
            changeActiveStatus,
            activeVersion,
            toggleInProgress,
            setError,
            map
        }) => () => {
            toggleInProgress();
            setError(null);
            changeActiveStatus(
                { clientId: map.clientId, mapId: map.id },
                map.version,
                !(map.version === activeVersion)
            )
                .then(() => {
                    toggleInProgress();
                })
                .catch(error => {
                    setError(error.message || 'Internet not available');
                    toggleInProgress();
                    setTimeout(() => {
                        setError(null);
                    }, 5000);
                });
        }
    })
);

const UnfrozenLink = FreezeButtonHandlers(
    ({ toggleFrozenStatus, inProgress, error, t }) => {
        if (inProgress) {
            return (
                <div className={theme.loadingWrap}>
                    <Loader theme={theme} active />
                </div>
            );
        }
        return (
            <DivWithPopover
                className={classnames(theme.activeBtn, theme.inactive, {
                    [theme.hasError]: error ? true : false
                })}
                onClick={toggleFrozenStatus}
                popOverContent={error || t('zoneMap.unfrozenError')}
                triggerPopoverOn={error ? undefined : PopoverTriggerEnum.Hover}
                isPopOverOpen={error ? true : undefined}
            >
                Freeze
            </DivWithPopover>
        );
    }
);

const FrozenLink = FreezeButtonHandlers(({ inProgress, error, t }) => {
    if (inProgress) {
        return (
            <div className={theme.loadingWrap}>
                <Loader theme={theme} active />
            </div>
        );
    }

    return (
        <DivWithPopover
            className={classnames(theme.activeBtn, theme.active)}
            popOverContent={error || t('zoneMap.frozenError')}
            triggerPopoverOn={error ? undefined : PopoverTriggerEnum.Hover}
            isPopOverOpen={error ? true : undefined}
        >
            Frozen
        </DivWithPopover>
    );
});

const COLUMNS = [
    {
        headerTextId: 'zoneMap.columnData.version',
        accessor: 'version',
        sortable: false,
        filterable: false,
        minWidth: 100,
        noCellPadding: true,
        Cell: props => {
            const {
                original: zoneMap,
                tdProps: {
                    rest: { changeActiveStatus, activeVersion, t }
                }
            } = props;
            const { version } = zoneMap;
            return (
                <div className={theme.versionCell}>
                    <Link
                        className={theme.cellText}
                        to={`/client/${zoneMap.clientId}/entity/map/${zoneMap.id}/version/${zoneMap.version}`}
                    >
                        {`Version ${zoneMap.version}`}
                    </Link>
                    {version === activeVersion ? (
                        <FrozenLink
                            t={t}
                            map={zoneMap}
                            activeVersion={activeVersion}
                            changeActiveStatus={changeActiveStatus}
                        />
                    ) : (
                        <UnfrozenLink
                            t={t}
                            map={zoneMap}
                            activeVersion={activeVersion}
                            changeActiveStatus={changeActiveStatus}
                        />
                    )}
                </div>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.verifyStatus',
        id: 'verified',
        Cell: props => {
            const {
                original: data,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { verified } = data;
            if (verified) {
                return t('zoneMap.columnData.verified');
            }
            return '-';
        },
        sortable: false
    },
    {
        headerTextId: 'zoneMap.columnData.zoneCount',
        accessor: 'summary.zonesCount',
        sortable: false
    },
    {
        headerTextId: 'zoneMap.columnData.riderCount',
        accessor: 'summary.uniqueRidersCount',
        sortable: false,
        settingsKey: 'showRiders'
    },
    {
        headerTextId: 'zoneMap.columnData.vehicleCount',
        accessor: 'summary.uniqueVehiclesCount',
        sortable: false,
        settingsKey: 'showVehicles'
    },
    {
        headerTextId: 'zoneMap.columnData.vehicleModelCount',
        accessor: 'summary.uniqueVehicleModelsCount',
        sortable: false,
        settingsKey: 'showVehicleModels'
    },
    {
        headerTextId: 'zoneMap.columnData.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: vehicle,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                vehicle?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'zoneMap.columnData.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

class MapVersionsTable extends PureComponent {
    columns: Array<Object>;

    _getColumns = () => {
        return COLUMNS.map((rawCol: Object) => {
            const { mapMetadataSettings, t } = this.props;
            if (
                rawCol.settingsKey &&
                !mapMetadataSettings[rawCol.settingsKey]
            ) {
                return null;
            }
            const { headerTextId } = rawCol;
            let column = rawCol;
            if (headerTextId) {
                column = {
                    ...rawCol,
                    headerText: t(headerTextId)
                };
            }
            return column;
        }).filter(column => column);
    };

    fetchVersions = mapId => {
        const { fetchZoneMapVersions, clientId } = this.props;
        fetchZoneMapVersions({ mapId, clientId });
    };

    onSubTableStateChange = () => {
        const { row } = this.props;
        const { original: zoneMap } = row;
        this.fetchVersions(zoneMap.map.id);
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const {
            changeActiveStatus,
            activeVersion,
            t,
            isGlobalPersonnel
        } = this.props;
        return {
            activeVersion,
            changeActiveStatus,
            t,
            theme,
            isGlobalPersonnel
        };
    };

    render() {
        const {
            zoneMapVersionData,
            isVersionTableLoading,
            activeVersion,
            t
        } = this.props;
        return (
            <PaginatedReactTable
                theme={theme}
                loading={isVersionTableLoading}
                activeVersion={activeVersion}
                data={
                    zoneMapVersionData
                        ? zoneMapVersionData.entities.reverse().toJS()
                        : []
                }
                totalPages={null}
                noDataText={!isVersionTableLoading ? t('noRowsFound') : ''}
                columns={this._getColumns()}
                defaultPageSize={null}
                onTableStateChange={this.onSubTableStateChange}
                getTdProps={this.getTdProps}
                stickyHeader={false}
                loaderSize={SizeEnum.Small}
            />
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isVersionTableLoading = ZoneMapSelectors.getIsEntityVersionsLoading(
        state,
        ownProps
    );
    const zoneMapVersionData = ZoneMapSelectors.getEntityVersions(
        state,
        ownProps
    );
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

    return {
        clientId,
        zoneMapVersionData,
        isVersionTableLoading,
        isGlobalPersonnel
    };
};

const ThemedMapVersionsTable = themr(null, theme)(MapVersionsTable);

export default withRouter(
    connect(mapStateToProps, {
        fetchZoneMapVersions: ZoneMapActions.fetchEntityVersions,
        changeActiveStatus: ZoneMapActions.changeActiveStatus
    })(ThemedMapVersionsTable)
);
